/**
 * String escaping utilities for safe JavaScript string interpolation.
 *
 * These utilities prevent XSS attacks when interpolating user-provided values
 * into JavaScript string literals (e.g., in inline <script> tags).
 *
 * AI-NOTE: This is JS-STRING escaping, not HTML escaping – the two threat
 * models are different and the escapers are not interchangeable. For HTML text
 * or attribute interpolation use `escapeHtml` / `escapeAttr` from
 * `../html-escape` (HAL-663), both re-exported from the package barrel.
 * A JSON value becoming a `<script>` element's text is a third threat model:
 * use `serializeJsonForScript`, never `escapeForJsString` on a JSON blob, which
 * escapes the JSON's own quotes and corrupts it.
 */

/**
 * Escape a string for safe interpolation into JavaScript string literals.
 * Prevents XSS by escaping characters that could break out of strings or scripts.
 *
 * Handles:
 * - Backslashes, quotes (single/double)
 * - Newlines, carriage returns
 * - Unicode line/paragraph separators (valid JSON, invalid JS)
 * - Script tag breakout prevention
 *
 * @example
 * ```typescript
 * // Safe interpolation into inline script
 * const script = `const name = '${escapeForJsString(userInput)}';`;
 * ```
 */
export function escapeForJsString(value: string): string {
  return value
    .replace(/\\/g, '\\\\') // Backslashes first
    .replace(/'/g, "\\'") // Single quotes
    .replace(/"/g, '\\"') // Double quotes
    .replace(/\n/g, '\\n') // Newlines
    .replace(/\r/g, '\\r') // Carriage returns
    .replace(/\u2028/g, '\\u2028') // Line separator (valid JSON, invalid JS)
    .replace(/\u2029/g, '\\u2029') // Paragraph separator
    .replace(/<\/(script)/gi, '<\\/$1'); // Prevent script tag breakout
}

/**
 * Serialize a value to JSON that is safe as the text of an inline `<script>`
 * element, whether a `type="application/ld+json"` block or a JS initializer.
 *
 * Every `<` becomes `\u003c`, which covers both ways the HTML parser can be
 * steered out of script data: `</script` closing the element early, and `<!--`
 * entering the escaped state where a later `<script` swallows the real close
 * tag. U+2028 and U+2029 become their escapes because they are line
 * terminators to a pre-ES2019 JS parser. JSON only admits these characters
 * inside string literals, so every replacement lands in one and the output
 * still `JSON.parse`s back to the input value.
 *
 * `space` is passed straight to `JSON.stringify`: the provider-site
 * `JsonLdScript` pretty-prints with 2, the landing copy stays compact.
 *
 * @example
 * ```tsx
 * <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonForScript(schema) }} />
 * ```
 */
export function serializeJsonForScript(value: unknown, space?: number): string {
  return JSON.stringify(value, null, space)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
